import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { BackButton } from 'src/components/shared/BackButton'
import { ContentCard } from 'src/components/shared/ContentCard'
import { StatusToneChip } from 'src/components/partner-driver/shared/StatusToneChip'
import { StatusTone } from 'src/constants/partnerDriver/enums'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { formatDriverName } from 'src/utils/partnerDriver/driverName'

interface Props {
  driver: PartnerDriverDetails
  backTo: { pathname: string; search?: string }
  backLabel: string
  /** Carried on the Back link, so the search query survives driver → steps → driver → search. */
  backState?: unknown
}

/** The driver's identity block, in the same white card as the sections below it. */
const PartnerDriverHeaderComponent = ({ driver, backTo, backLabel, backState }: Props) => {
  // A generic fallback for a driver with no name on file — cosmetic, not fabricated data.
  const name = formatDriverName(driver.firstName, driver.lastName) || 'Unnamed driver'
  const partnerName = driver.partner?.displayName || driver.partner?.name || ''

  return (
    <ContentCard className={tw`flex flex-col gap-4`} dataTestId="partner-driver-header">
      <BackButton
        label={backLabel}
        to={backTo}
        state={backState}
        dataTestId="partner-driver-back-link"
      />
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="h2">{name}</Typography>
        <div css={tw`flex flex-row flex-wrap items-center gap-2`}>
          {partnerName ? (
            <Typography variant="body2" css={tw`text-shades-500`}>
              {partnerName}
            </Typography>
          ) : null}
          <StatusToneChip
            tone={driver.isActive ? StatusTone.Success : StatusTone.Neutral}
            text={driver.isActive ? 'Active' : 'Inactive'}
          />
          <StatusToneChip
            tone={driver.onboardedAt ? StatusTone.Success : StatusTone.Neutral}
            text={driver.onboardedAt ? 'Onboarded' : 'Not onboarded'}
          />
        </div>
      </div>
    </ContentCard>
  )
}

export const PartnerDriverHeader = memo(PartnerDriverHeaderComponent)
